import { CustomDropdown, CustomDropdownItem } from "@/components/ui/dropdown";
import { Progress } from "@/components/ui/progress/progress";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/20/solid";
import { SceneInsight } from "@/api/studio/insights/types/insight.type";
import { SceneMetric } from "../stores/use-reports-scene-store";
import { getSceneRenderUrl } from "../utils/get-scene-render-url";
import { getMetricColorValue } from "../utils/get-metric-color-value";
import { getPowConfig } from "../utils/get-pow-config";
import { getSceneMetricValue, formatSceneMetricValue } from "../utils/scene-metric-utils";

/* Prop Types */
interface SceneComparisonColumnProps {
  projectId: string;
  studyId: string;
  allScenes: SceneInsight[];
  selectedSceneKey: string | null;
  onSelectScene: (sceneKey: string) => void;
  onImageClick?: (sceneKey: string) => void;
  metric: SceneMetric;
}

/*
 * SceneComparisonColumn Component
 * Displays a single scene column in the comparison view with dropdown selector,
 * heatmap image, and AOI list.
 */
export const SceneComparisonColumn = ({ projectId, studyId, allScenes, selectedSceneKey, onSelectScene, onImageClick, metric }: SceneComparisonColumnProps) => {
  /* Find the selected scene */
  const selectedScene = allScenes.find((scene) => scene.key === selectedSceneKey);
  const regions = selectedScene?.attention?.regions ?? [];

  /* Calculate progress bar value using metric-specific color mapping */
  const getProgressValue = (value: number) => {
    return getMetricColorValue(value, metric);
  };

  /* POW Indicator component */
  const PowIndicator = ({ value }: { value: number }) => {
    const { bg, icon: Icon } = getPowConfig(value);
    return <Icon className="size-4" style={{ color: bg }} />;
  };

  return (
    <div className="flex flex-col">
      {/* Scene Selector Dropdown */}
      <CustomDropdown
        triggerBtn={
          <button className="w-full flex items-center justify-between h-auto py-3 px-4 border-2 border-base-300 bg-base-100 hover:bg-base-200 rounded-lg cursor-pointer">
            <span className="truncate text-left flex-1">{selectedScene?.assetName || "Select a scene"}</span>
            <ChevronDownIcon className="size-5 shrink-0 ml-2" />
          </button>
        }
        triggerBtnClassname="p-0 w-full"
        dropdownClassname="w-full"
        listClassname="w-full max-h-[300px]"
      >
        {allScenes.map((scene) => {
          const isSelected = scene.key === selectedSceneKey;
          return (
            <CustomDropdownItem key={scene.key} onClick={() => onSelectScene(scene.key)} className={isSelected ? "bg-base-200 pointer-events-none opacity-60" : ""}>
              <div className="flex items-center justify-between w-full gap-2">
                <span className="truncate">{scene.assetName}</span>
                {isSelected && <CheckIcon className="size-4 text-primary shrink-0" />}
              </div>
            </CustomDropdownItem>
          );
        })}
      </CustomDropdown>

      {/* Heatmap Image */}
      {selectedScene ? (
        <div className="mt-4 cursor-pointer" onClick={() => onImageClick?.(selectedScene.key)}>
          <img
            src={getSceneRenderUrl(projectId, studyId, selectedScene.key, { metric, visualisation: "heat" as const, showAOI: true, showGazePath: false }) + `&t=${Date.now()}`}
            alt={selectedScene.assetName}
            className="w-full h-auto rounded-lg object-contain aspect-[4/3] bg-base-300 hover:opacity-90 transition-opacity"
          />
        </div>
      ) : (
        <div className="mt-4 w-full aspect-[4/3] rounded-lg bg-base-200" />
      )}

      {/* Number of AOIs */}
      <div className="flex justify-between items-center py-4 px-4 mt-2">
        <p className="font-semibold">Number of AOIs</p>
        <p className="font-semibold">{regions.length}</p>
      </div>

      {/* AOI Header */}
      <div className="py-3 px-4 border-y border-base-300 bg-base-200">
        <p className="text-xs text-base-content/60 font-bold">AOI</p>
      </div>

      {/* AOI List */}
      <div className="flex flex-col">
        {regions.map((region) => {
          const metricValue = getSceneMetricValue(region, metric);
          const progressValue = getProgressValue(metricValue);
          const displayValue = formatSceneMetricValue(metricValue, metric);
          const isPow = metric === "pow";

          return (
            <div key={region.id} className="flex items-center justify-between gap-3 py-3 px-4 border-b border-base-300">
              {/* Color indicator + AOI Name */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="size-2.5 rounded-xs shrink-0" style={{ backgroundColor: region.color || "#F2318A" }} />
                <p className="text-sm font-semibold truncate">{region.label}</p>
              </div>

              {/* Progress bar/POW indicator + Value */}
              <div className="flex items-center gap-3 shrink-0">
                {isPow ? <PowIndicator value={metricValue} /> : <Progress value={progressValue} className="w-24" />}
                <p className="text-sm font-medium min-w-[40px] text-right">{displayValue}</p>
              </div>
            </div>
          );
        })}

        {/* Empty state */}
        {regions.length === 0 && selectedScene && (
          <div className="py-8 text-center text-base-content/50">
            <p className="text-sm">No AOIs found</p>
          </div>
        )}

        {/* No scene selected state */}
        {!selectedScene && (
          <div className="py-8 text-center text-base-content/50">
            <p className="text-sm">Select a scene to view AOIs</p>
          </div>
        )}
      </div>
    </div>
  );
};
